/**
 * Feature-launched rounds (daily puzzle, tournament, event, beat, duel): the contract a
 * feature hands to the play scene instead of a campaign level number, and the rule overrides
 * it may ask for. Pure and Phaser-free so the rules are unit-tested; PlayScene applies them.
 *
 *   startCustomRound(a, { mode: 'tournament', level, seed, rules: { boosters: 'none' }, submit })
 */
import type { LevelV1, Round, RoundEvent, RoundOutcome } from '@mp/core';
import { BOOSTERS, type BoosterKey } from '../config/content.js';
import type { RoundBody } from './dto.js';
import { featureUnlocked } from './progress.js';
import { BOOSTER_ROUND_CAP, type RoundMode, type RoundRecord } from './rounds.js';
import type { PlayerState } from './state.js';

export type CustomMode = 'daily' | 'tournament' | 'event' | 'beat' | 'help' | 'duel';

/**
 * The `mode` the round body carries (event rounds are plain level rounds of the event's level
 * files; `help` is a friend's restock order, SO-01.2, played from a share link in the app).
 */
export const WIRE_MODE: Record<CustomMode, RoundMode> = { daily: 'daily', tournament: 'tournament', event: 'level', beat: 'beat', help: 'help', duel: 'duel' };

export interface CustomRules {
  /** 'normal' (default): the player's boosters with the usual caps; 'onePerLevel': one booster use in the whole round; 'none': no booster bar */
  boosters?: 'none' | 'onePerLevel' | 'normal';
  /** offer the rescue on a fail (default true); 'ad': only the rewarded-ad continue (the Rush, launch plan 2L) */
  rescue?: boolean | 'ad';
  /** GP-09 assist after repeated fails of this level id (default false) */
  assist?: boolean;
}

/** What `submit` gets once the round ended (won or failed for good). */
export interface CustomSubmission {
  rec: RoundRecord;
  /** RoundRequestR1 body (mode = WIRE_MODE[mode]); send it to the feature's endpoint */
  body: RoundBody;
  outcome: RoundOutcome;
  round: Round;
}

export interface CustomResultLine {
  /** already localised */
  label: string;
  value: string;
  testId?: string;
  /** highlight (e.g. a new best, a rank gained) */
  good?: boolean;
}

/** The feature's answer to a finished round; the result screen shows it. */
export interface CustomResult {
  /** replaces the win/fail title (already localised) */
  title?: string;
  /** rows under the stars: points, leaderboard delta, rank… */
  lines?: CustomResultLine[];
  /** coins the round paid (shown; the feature adopts the server snapshot itself) */
  coins?: number;
  /** result: 'custom' → this feature view is shown instead of the standard screen */
  view?: { kind: string; props?: unknown };
  /** show "Try again" (restarts the same level and seed) */
  retry?: boolean;
  /** after the result: open this feature view on the map (e.g. back to the tournament screen) */
  next?: { open: string; props?: unknown };
}

export interface CustomRound {
  mode: CustomMode;
  level: LevelV1;
  seed: number;
  /** HUD label instead of "Level n" (already localised) */
  label?: string;
  rules?: CustomRules;
  /**
   * Called once when the round ends. Record/submit it (offline-first if the feature allows)
   * and answer what the result screen shows. Throwing shows the standard screen with an error toast.
   */
  submit: (s: CustomSubmission) => Promise<CustomResult>;
  /** live hook after every applied action (duel progress relay); must be cheap and never throw */
  onEvents?: (events: RoundEvent[], round: Round) => void;
  /** awaited after the intro, before the play clock starts (duel: send ready, wait for duel.start) */
  beforeStart?: () => Promise<void>;
  /** 'standard' (default): the core result screen with the answer's lines; 'custom': the answer's `view` */
  result?: 'standard' | 'custom';
}

export const DEFAULT_RULES: Required<CustomRules> = { boosters: 'normal', rescue: true, assist: false };

export function rulesOf(c: CustomRound | null | undefined): Required<CustomRules> {
  if (!c) return { ...DEFAULT_RULES, assist: true };
  return { ...DEFAULT_RULES, ...c.rules };
}

/** The booster buttons the play bar shows (empty = no bar). */
export function boosterKeys(rules: Required<CustomRules>, s: PlayerState): BoosterKey[] {
  if (rules.boosters === 'none' || !featureUnlocked(s, 'boosters')) return [];
  return BOOSTERS.filter((k) => featureUnlocked(s, k === 'magnet' || k === 'extraSlot' ? k : 'boosters'));
}

/**
 * Why booster `k` cannot be used now (null = allowed): the server's per-round replay budget,
 * or the custom round's one-booster rule.
 */
export function boosterBlock(rules: Required<CustomRules>, used: Partial<Record<BoosterKey, number>>, k: BoosterKey): 'none' | 'onePerLevel' | 'roundCap' | null {
  if (rules.boosters === 'none') return 'none';
  if (rules.boosters === 'onePerLevel' && Object.values(used).some((n) => (n ?? 0) > 0)) return 'onePerLevel';
  const cap = BOOSTER_ROUND_CAP[k];
  if (cap !== undefined && (used[k] ?? 0) >= cap) return 'roundCap';
  return null;
}

/** GP-09 assist level for this round (0 when the rules turn it off). */
export function assistFor(rules: Required<CustomRules>, fails: number): number {
  if (!rules.assist) return 0;
  return fails >= 3 ? Math.min(2, fails - 2) : 0;
}
